import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnChanges, OnDestroy, OnInit } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { EventsService } from '@omni/services/events/events.service';
import { SharedModule } from '@omni/shared/shared.module';
import { DB_KEY_PREFIXES } from '@omni/config/pouch-db.config';
import { DiskService } from '@omni/services/disk/disk.service';
import { CallPlanOfflineService } from '@omni/services/call-plan/call-plan.offline.service';
import { TranslateService } from '@ngx-translate/core';
import { IndSectionHeaderViewDataModel } from '@omni/models/indSectionHeaderDataModel';
import { NavigationService, PageName } from '@omni/services/navigation/navigation.service';
import { CustomerCallPlanOverView } from '@omni/components/customer-call-plans/customer-call-plan-overview/customer-call-plan-overview';

@Component({
  standalone: true,
  imports: [SharedModule, CustomerCallPlanOverView],
  selector: 'contact-call-plan',
  templateUrl: './contact-call-plan.html',
  styles: ['.no-data-message { padding-bottom: 20px; }'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ContactCallPlanComponent implements OnChanges, OnInit, OnDestroy {
  @Input() contactId: string;
  @Input() preview = false;
  @Input() period = 'call-plan-current';
  public loadedContactId: string;
  public contactCallPlan: any;
  public emptyHeader: IndSectionHeaderViewDataModel;
  private readonly destroy$ = new Subject<void>();
  private isDestroyed = false;
  // Preview and list page can be open together; the state seen before the first one opened is restored when the last closes.
  private static openInstances: ContactCallPlanComponent[] = [];
  private static callPlanStateBeforeOpen: { selectedCallPlan: any; callPlanMode: string; segmentCallPlans: any[] };

  constructor(
    private readonly callPlanService: CallPlanOfflineService,
    private readonly disk: DiskService,
    private readonly events: EventsService,
    private readonly cd: ChangeDetectorRef,
    private readonly translate: TranslateService,
    private readonly navService: NavigationService,
  ) {}

  ngOnChanges() {
    this.loadedContactId = undefined;
    this.loadAndSelectContactCallPlan();
  }

  ngOnInit() {
    const openListControl = { id: 'open-call-plan-list', iconName: 'chevron-forward-outline', isDisabled: true };
    this.emptyHeader = { id: 'contact-call-plan-empty-header', title: this.translate.instant('CALL_PLANS'), controls: this.preview ? [openListControl] : [] };
    if (!ContactCallPlanComponent.openInstances.length) {
      const { selectedCallPlan, callPlanMode, segmentCallPlans } = this.callPlanService;
      ContactCallPlanComponent.callPlanStateBeforeOpen = { selectedCallPlan, callPlanMode, segmentCallPlans };
    }
    ContactCallPlanComponent.openInstances.push(this);
    this.callPlanService.setCallPlanMode('mine');
    this.events.observe('callPlanActivitiesUpdated').pipe(takeUntil(this.destroy$)).subscribe(() => this.reloadAndRefreshOverview());
  }

  ngOnDestroy() {
    this.isDestroyed = true;
    this.destroy$.next();
    this.destroy$.complete();
    const openInstances = ContactCallPlanComponent.openInstances;
    openInstances.splice(openInstances.indexOf(this), 1);
    if (openInstances.length) {
      openInstances[openInstances.length - 1].reloadAndRefreshOverview();
      return;
    }
    const { selectedCallPlan, callPlanMode, segmentCallPlans } = ContactCallPlanComponent.callPlanStateBeforeOpen;
    this.callPlanService.selectedCallPlan = selectedCallPlan;
    this.callPlanService.setCallPlanMode(callPlanMode);
    this.callPlanService.segmentCallPlans = segmentCallPlans;
  }

  private async reloadAndRefreshOverview() {
    await this.loadAndSelectContactCallPlan();
    if (!this.isDestroyed) this.events.publish('callPlanListUpdated');
  }

  public openCallPlanList(period?: string) {
    this.navService.pushChildNavPageWithPageTracking(PageName.ContactCallPlanListComponent, PageName.ContactCallPlanListComponent, PageName.ContactPageComponent, { callPlanListPeriod: period });
  }

  private async loadAndSelectContactCallPlan() {
    const contactId = this.contactId;
    const myCallPlans = (await this.disk.retrieve(DB_KEY_PREFIXES.MY_POSITON_CALL_PLANS))?.raw ?? [];
    if (this.isDestroyed || contactId !== this.contactId) return;
    this.callPlanService.segmentCallPlans = myCallPlans;
    const contactRepCallPlans = myCallPlans.filter(repCallPlan => repCallPlan.contactId === contactId);
    this.contactCallPlan = this.callPlanService.aggregateRepCallPlans(contactRepCallPlans, 'contact')[0];
    this.callPlanService.selectedCallPlan = this.contactCallPlan;
    this.loadedContactId = contactId;
    this.cd.markForCheck();
  }
}
